/*
  ba-chip-active.js
  WHAT: Derives `is-active` state on chip strips from the current URL.
        Replaces hardcoded Liquid `is-active` which never matched the
        applied filter and left users unable to tell which filter they
        were viewing.
  WHY:  CLAUDE.md rule 24 (2026-06-11) — active state must be DERIVED,
        not hardcoded. Owner-triggered: on a filtered URL like
        /collections/earbuds?filter.p.tag=ia_nav_anc the "All" chip
        stayed visually active while no chip reflected the actual filter.
  WHEN: 2026-06-11.

  Coverage:
    - Collection page top chip bar (`.ba-coll__chip-bar-inner .ba-coll__chip`)
    - Mega menu chip strips (`.ba-mega__chips .ba-mega__chip`)
    - Any future chip strip with the conventions above

  Behaviour:
    - On DOMContentLoaded + on any history change (back/forward), sync.
    - For each chip strip:
      - Read chip's href, extract filter.p.tag value (if any) + pathname.
      - Compare against window.location.search filter.p.tag + pathname.
      - Chip matches → add .is-active + aria-current="true".
      - No match → remove .is-active + remove aria-current entirely.
        (aria-current, not aria-pressed: chips are <a href> links and
        aria-pressed is only valid on a button role — axe-core CRITICAL.
        "Not current" is expressed by the attribute being ABSENT.)
    - "All" chips (data-filter="") are active when no filter.p.tag is set
      AND the current path matches the chip's href path (so the All chip on
      Earbuds doesn't light up on a Speakers page).
*/

(function () {
  'use strict';

  var STRIP_SELECTORS = [
    '.ba-coll__chip-bar-inner',
    '.ba-mega__chips'
  ];
  var CHIP_SELECTOR = '.ba-coll__chip, .ba-mega__chip';

  function getFilterTag(searchParams) {
    return searchParams.get('filter.p.tag') || '';
  }

  /*
    Owner 2026-08-25: "when i go to the headphones page to choose a headphone the any
    price and 200+ are both highlighted. when the page first loads for collections it
    should be any price. these are mutually exclusive" - and the same on speakers,
    soundbars and earbuds.

    Root cause: this used to read ONLY `filter.v.price.lte`. Every band except the top
    one carries an lte, but "$200+" is open-ended - it carries `gte=200` and no lte.
    So its chipPriceLte came back empty, the chip looked to this function like a chip
    with no filter at all, and it matched the same "nothing is applied" branch as
    "Any price". Both lit up on a bare collection URL, on every collection.

    Both bounds are read now and compared as a pair, so each band matches only its own
    URL and "Any price" wins when neither bound is present.
  */
  function getFilterPrice(searchParams) {
    return {
      gte: searchParams.get('filter.v.price.gte') || '',
      lte: searchParams.get('filter.v.price.lte') || ''
    };
  }

  function hasPrice(price) {
    return price.gte !== '' || price.lte !== '';
  }

  function samePrice(a, b) {
    return a.gte === b.gte && a.lte === b.lte;
  }

  /*
    A tag filter reaches us in TWO URL shapes and they are the SAME state:
      path  form  /collections/earbuds/ia_nav_anc              <- what the chips emit
      query form  /collections/earbuds?filter.p.tag=ia_nav_anc <- what the quiz emits
    Until 2026-09-16 only the path form ever lit a chip, because the comparison below
    matched whole pathnames. Anyone arriving from the category quiz therefore landed on
    a correctly filtered grid with NO chip marked at all - precisely the rule-24 failure
    that deriving state is meant to prevent, and invisible unless you test that URL shape.

    normalise() collapses both shapes to { path, tag } so nothing downstream has to care
    which one produced the URL. Multi-tag path URLs (/collections/x/tag1+tag2) keep their
    full tag string, so they still compare exactly as before.
  */
  function normalise(url) {
    var path = url.pathname.replace(/\/$/, '');
    var tag = getFilterTag(url.searchParams);
    if (!tag) {
      var seg = path.split('/');
      if (seg.length > 3 && seg[1] === 'collections') {
        tag = seg.slice(3).join('/');
        path = '/' + seg[1] + '/' + seg[2];
      }
    }
    return { path: path, tag: tag };
  }

  function syncOne(strip) {
    var current = new URL(window.location.href);
    var currentNorm = normalise(current);
    var currentPath = currentNorm.path;
    var currentTag = currentNorm.tag;
    var currentPrice = getFilterPrice(current.searchParams);
    // The QUERY-string tag specifically, as distinct from the normalised one above.
    var queryTag = getFilterTag(current.searchParams);

    var chips = strip.querySelectorAll('a');
    chips.forEach(function (chip) {
      if (!chip.classList.contains('ba-coll__chip') &&
          !chip.classList.contains('ba-mega__chip')) return;

      if (chip.hasAttribute('data-active-source')) {
        /*
          Liquid derived this chip from `current_tags`, and it is RIGHT to own it: the
          href is a TOGGLE url - an active facet links to the state WITHOUT its own tag -
          so deriving from href would mark the applied facet inactive and an unapplied
          one active. That is why this early-return exists.

          But Shopify populates `current_tags` only for the PATH form. On a query-form
          URL Liquid is blind and EVERY facet renders inactive, which is what a category
          quiz landing produced: a correctly filtered grid with no chip marked at all.
          The 2026-09-16 normalise() fix reached the mega chips and skipped these.

          So re-derive here in that one case, and do it from the chip's OWN tag
          (`data-filter-tag`) rather than its toggle href, which sidesteps the inversion
          the comment above warns about. On path-form URLs we still defer to Liquid, so
          there is no flash of unstyled state on the common path.
        */
        if (!queryTag) return;
        var ownTag = chip.getAttribute('data-filter-tag') || '';
        var onNow = ownTag !== '' && queryTag.split('+').indexOf(ownTag) !== -1;
        chip.classList.toggle('is-active', onNow);
        if (onNow) chip.setAttribute('aria-current', 'true');
        else chip.removeAttribute('aria-current');
        return;
      }

      /*
        "Help me choose" is a guide link, not a filter. Its href is the bare collection
        URL, identical to the "All" chip's, so the reset branch below lit BOTH at once -
        two current items in one group, which is meaningless to a screen reader.
        It carries no filter state, so it is never current.
      */
      if (chip.classList.contains('ba-mega__chip--guide')) return;

      chip.classList.remove('is-active');
      chip.removeAttribute('aria-current');

      var href = chip.getAttribute('href');
      if (!href) return;

      var chipUrl;
      try { chipUrl = new URL(href, window.location.origin); }
      catch (e) { return; }

      var chipNorm = normalise(chipUrl);
      var chipPath = chipNorm.path;
      var chipTag = chipNorm.tag;
      var chipPrice = getFilterPrice(chipUrl.searchParams);

      /*
        Price and category are INDEPENDENT axes and each has its own reset, so they
        are judged separately. A single "nothing applied anywhere" test made the two
        resets fight: with a price band on the URL, the category "All" chip switched
        off even though no category filter was applied.
      */
      var isActive = false;

      /*
        ORDER MATTERS: the price axis is decided FIRST, by class, before any tag test.

        A price chip's href is `request.path` plus the band, so it carries whatever TAG
        PATH is currently applied. Once normalise() started extracting that path tag,
        the "Any price" chip began looking like a tag chip to the generic `chipTag`
        branch below and lit alongside the active band - reintroducing exactly the bug
        the owner reported on 2026-08-25 ("any price and 200+ are both highlighted...
        these are mutually exclusive"). Caught by DOCS/AUDITS/price-chip-test.js
        ("headphones · tag + $200+"), which is why that gate exists.
      */
      if (chip.classList.contains('ba-coll__chip--price')) {
        if (hasPrice(chipPrice)) {
          // A specific band. Must match the band AND the tag context it sits in.
          isActive = chipPath === currentPath &&
                     chipTag === currentTag &&
                     samePrice(chipPrice, currentPrice);
        } else {
          /*
            "Any price" - the price reset. Deliberately TAG-AGNOSTIC, as originally
            documented: it answers "is a price band applied?" and nothing else. Requiring
            the tag to match too would leave it dark on query-form URLs, whose tag lives
            in the query string and so cannot appear in this chip's `request.path` href -
            i.e. a quiz landing would show no price state at all.
          */
          isActive = chipPath === currentPath && !hasPrice(currentPrice);
        }
      } else if (chipTag) {
        isActive = chipTag === currentTag && chipPath === currentPath;
      } else {
        // "All" - the category reset. Price-agnostic: the Price group has its own.
        isActive = chipPath === currentPath && !currentTag;
      }

      if (isActive) {
        chip.classList.add('is-active');
        chip.setAttribute('aria-current', 'true');
      }
    });
  }

  function syncAll() {
    STRIP_SELECTORS.forEach(function (sel) {
      document.querySelectorAll(sel).forEach(syncOne);
    });
  }

  /*
    Collection-grid tag filter — owner 2026-06-11.
    Shopify's `?filter.p.tag=X` URL param requires Search & Discovery
    storefront filters to be configured per-collection. On this shop, that
    config isn't set up, so `collection.products` returns the unfiltered
    set and 9 products render on a "filtered" earbuds page when only 2
    should match. This client-side filter reads the URL tag, finds product
    tiles with matching `data-tags`, and hides non-matchers via display:none
    so CSS Grid auto-reflows the remaining tiles top-left + adjacent.

    Tag-match logic mirrors site-header-mega.js — checks for hyphen,
    underscore, prefixed (`ia_nav_X`) and bare forms.
  */
  function filterCollectionGrid() {
    /* SUPERSEDED BY ba-coll-live-filter.js. Stand down where that is running.

       This is the 2026-06-11 single-tag implementation. ba-coll-live-filter.js replaced it on
       2026-09-30 with multi-select, and both write style.display on the same .ba-ptile-wrap
       elements - but they read `filter.p.tag` incompatibly. This one treats the parameter as
       ONE literal tag; the newer one splits it on commas.

       So on a multi-tag URL like ?filter.p.tag=f_led,f_mic_wired this function matches no tile
       and hides every product. Deferred scripts run in document order, so on first paint the
       newer file runs second and repairs it - but this file also binds `pageshow`, and a back
       navigation or bfcache restore re-runs THIS and empties the grid with nothing to undo it.

       Kept rather than deleted so collection surfaces that do not load the live filter keep
       working. */
    if (window.__baLiveFilter) return;

    var url = new URL(window.location.href);
    var rawTag = url.searchParams.get('filter.p.tag');
    var grid = document.querySelector('.ba-coll__grid');
    if (!grid) return;

    // CRITICAL FIX 2026-06-12: data-tags is on the <a class="ba-ptile"> link,
    // but the GRID CHILD is the wrapper .ba-ptile-wrap. Hiding the <a> left
    // the wrap visible, holding its original grid cell — visible tiles
    // couldn't reflow to top-left. Now we walk from the [data-tags] element
    // up to the direct grid child and hide THAT, so CSS Grid auto-place
    // collapses the empty cells.
    var tiles = grid.querySelectorAll('[data-tags]');
    if (!tiles.length) {
      grid.setAttribute('data-filter-applied', 'no-tiles');
      grid.removeAttribute('data-filter-pending');
      return;
    }

    // Helper: walk from a descendant up to the direct child of `grid`.
    function gridChildOf(el) {
      var cur = el;
      while (cur && cur.parentElement && cur.parentElement !== grid) {
        cur = cur.parentElement;
      }
      return (cur && cur.parentElement === grid) ? cur : el;
    }

    // No filter param: clear any previously-hidden grid children + bail.
    if (!rawTag) {
      tiles.forEach(function (t) {
        var child = gridChildOf(t);
        child.style.removeProperty('display');
      });
      grid.setAttribute('data-filter-applied', 'none');
      grid.removeAttribute('data-filter-pending');
      return;
    }

    // Normalize the URL filter value to underscore form (open-ear → open_ear)
    // so we can match against tile tags consistently. Tile tags use BOTH
    // raw values (open-ear) AND prefixed forms (ia_nav_open_ear, f_form_open-ear).
    // Strategy: a tile tag matches the filter if it's identical OR ends with
    // "_<filter>" (catches the prefixed forms regardless of which prefix).
    // Bug 2026-06-18: previously only exact + dash/underscore variants were
    // checked, which missed prefixed tile tags entirely. Owner: "earbuds then
    // open-ear — the Open X card stays in the same spot instead of re-rendering
    // in the top-left spot" — Open X has `ia_nav_open_ear` so the exact match
    // failed and Open X was being hidden by the filter rather than shown.
    var tagNormalized = rawTag.trim().toLowerCase().replace(/-/g, '_');
    var tagSuffix = '_' + tagNormalized;

    /*
      Count UNIQUE grid children, not [data-tags] matches. product-tile.liquid emits
      data-tags twice per product - on .ba-ptile-wrap and again on the inner .ba-ptile
      anchor - so `visibleCount++` per match reported double (4 for 2 products). Hiding
      is unaffected, because both matches resolve to the same child and set the same
      style; only this diagnostic attribute was wrong. Found 2026-09-16 by the same
      double-count that was making DOCS/AUDITS/filter-test.js fail on a working grid.
    */
    var shownChildren = new Set();
    tiles.forEach(function (tile) {
      var tags = (tile.getAttribute('data-tags') || '').toLowerCase().split(',');
      var hit = false;
      for (var i = 0; i < tags.length; i++) {
        var t = tags[i].trim().replace(/-/g, '_');
        if (t && (t === tagNormalized || t.indexOf(tagSuffix, t.length - tagSuffix.length) !== -1)) {
          hit = true; break;
        }
      }
      var child = gridChildOf(tile);
      if (hit) {
        child.style.removeProperty('display');
        shownChildren.add(child);
      } else {
        child.style.display = 'none';
      }
    });

    // Mark grid state for diagnostic + audit. Owner 2026-06-11: do NOT set
    // inline grid-template-columns — tiles stay at their CSS-defined width
    // (repeat(4, 1fr) at desktop). Visible tiles sit in cells 1, 2, ... at
    // 25% width each (left-aligned, adjacent). Remaining cells are unused
    // grid space — owner explicit: "I don't want columns 50% each. tiles
    // to be next to each other with left alignment."
    grid.setAttribute('data-filter-applied', rawTag);
    grid.setAttribute('data-filter-visible-count', String(shownChildren.size));
    grid.removeAttribute('data-filter-pending');
    grid.style.removeProperty('grid-template-columns');

    // Update the "All" chip count if the visible chip-bar reflects the
    // filtered subset — owner's "first item top-left, next adjacent, no gaps"
    // behaviour is automatic via CSS Grid auto-placement once non-matchers
    // are display:none.
    var allChipCount = document.querySelector('.ba-coll__chip-bar-inner .ba-coll__chip-count');
    if (allChipCount) {
      var coll = document.querySelector('.ba-coll__chip-bar-inner .ba-coll__chip[data-filter=""]');
      if (coll && coll.classList.contains('is-active')) {
        // On "All" view, leave the count as the original collection size.
      }
    }
  }

  function runAll() {
    syncAll();
    filterCollectionGrid();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAll);
  } else {
    runAll();
  }

  // Re-sync on history navigation (back/forward) since chip strips
  // and grids are not re-rendered by the browser on bfcache restores.
  window.addEventListener('pageshow', runAll);
})();
